import { ChangeDetectionStrategy, Component, HostBinding, inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { API_LEVERAGED_TOKEN_TYPE, VERIFICATION_MODAL_ROUTE } from "@helpers/constants";
import { convertFiatToTradable } from "@helpers/tradable-convert";
import { isSafari } from "@helpers/utils";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { SegmentService } from "@main-services/segment.service";
import { UserService } from "@main-services/user.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { Offer, PaymentOption } from "@models";
import { IndexWallet } from "@models/asset/index/index_wallet";
import { Feature } from "@models/feature-list";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { VoucherOffer } from "@models/voucher_offer";
import { FiatDepositEntryService } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit-entry.service";
import { OrderFlow } from "@modules/limit-orders/interfaces/limit-order-rudderstack.model";
import { LimitOrdersTrackingFacade } from "@modules/limit-orders/services/orders-rudderstack.facade";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { VerificationModalFacade } from "@modules/verification/modal/state/verification-modal.facade";
import { combineLatest, EMPTY, Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import { AccountService } from "../../../account/services/account.service";
import { CustomModal } from "../../../core/components/base-modals";
import { TradeBuyComponent } from "../trade-buy-modal/trade-buy-modal.component";
import { TradeSavingsComponent } from "../trade-savings-modal/trade-savings-modal.component";
import { TradeSellComponent } from "../trade-sell-modal/trade-sell.component";
import { TradeSwapComponent } from "../trade-swap-modal/trade-swap.component";
import { TradeVoucherComponent } from "../trade-voucher-modal/trade-voucher.component";
import { MarginTradingFacade } from "@modules/margin-trading/services/margin-trading.facade";
import { RudderstackService } from "@main-services/rudderstack.service";
import { StakingEntryHelperService } from "@modules/staking/services/staking-entry.helper.service";

@Component({
	templateUrl: "./trade-type-decision-modal.component.html",
	styleUrls: ["./trade-type-decision-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TradeTypeDecisionModalComponent extends CustomModal implements OnInit {
	private userFiatFundService = inject(UserFiatFundsService);
	private readonly stakingEntryHelper = inject(StakingEntryHelperService);
	private readonly statsigService = inject(StatsigClientService);

	@HostBinding("class") public cssClass = "modal-steps";

	public featureList = Feature;

	public canBuy: boolean = false;
	public canSell: boolean = false;
	public isStakingEnabled$: Observable<boolean> = EMPTY;
	public showCheckoutFlow$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.CHECKOUT_FLOW_ENABLED);

	public readonly shouldDisplayLeveragePill$ = combineLatest({
		mtBaseFlag: this.marginTradingFacade.marginTradingEnabled$(),
		mtBuyFlag: this.marginTradingFacade.marginTradingBuyEnabled$(),
		leverageFlag: this.statsigService.isFeatureActive(FEAT_GATES.LEVERAGE)
	}).pipe(
		map(({ mtBaseFlag, mtBuyFlag, leverageFlag }) => {
			if (leverageFlag) {
				return true;
			}

			return mtBaseFlag && mtBuyFlag;
		})
	);

	public constructor(
		private tradableListService: TradableListService,
		private accountService: AccountService,
		private masterdataService: MasterdataService,
		private readonly walletService: TradableWalletListService,
		private userService: UserService,
		private verificationModalFacade: VerificationModalFacade,
		private stakingFacade: StakingFacade,
		private router: Router,
		private segmentService: SegmentService,
		private rudderstackService: RudderstackService,
		protected launchDarklyService: LaunchDarklyService,
		private readonly fiatDepositEntryService: FiatDepositEntryService,
		private readonly limitOrdersTrackingFacade: LimitOrdersTrackingFacade,
		private readonly marginTradingFacade: MarginTradingFacade
	) {
		super();
	}

	public get isMuted(): boolean {
		return !this.canSell || this.unverified;
	}

	public get unverified(): boolean {
		return this.userService.isUnverified;
	}

	public get isVerificationMandatory(): boolean {
		return this.userService.isVerificationMandatory;
	}

	public get savingsDisabled(): boolean {
		return this.accountService.account.getSavingsDisabled();
	}

	public ngOnInit(): void {
		this.canBuy = this.hasSufficientFundsBuy();
		this.canSell = this.hasSufficientFundsSell();
		this.isStakingEnabled$ = this.stakingFacade
			.isStakingEnabled$()
			.pipe(map((isEnabled: boolean) => isEnabled && !this.isVerificationMandatory));
		this.rudderstackService.page({
			name: "Trade Screen",
			overridePath: "/trade"
		});
	}

	public answer(type: TRADE_TYPE): void {
		if (TRADE_TYPE.BUY || TRADE_TYPE.CHECKOUT || TRADE_TYPE.LEVERAGE) {
			this.limitOrdersTrackingFacade.updateFlow(OrderFlow.TradeSheet);
		}

		const offer = new Offer();
		offer.setType(type);

		switch (type) {
			case TRADE_TYPE.BUY: {
				if (!this.canBuy) {
					this.fiatDepositEntryService.openDepositNow();
				} else {
					this.showModal(TradeBuyComponent, offer);
				}
				return;
			}
			case TRADE_TYPE.SELL: {
				if (!this.canSell) {
					return;
				}
				this.showModal(TradeSellComponent, offer);
				return;
			}
			case TRADE_TYPE.SWAP: {
				if (!this.canSell) {
					return;
				}
				this.showModal(TradeSwapComponent, offer);
				return;
			}
			case TRADE_TYPE.RECURRING_BUY: {
				this.segmentService.track({
					name: "Savings Button Clicked",
					properties: {
						category: "Trading interaction",
						screen_it_happened_on: "home_screen"
					}
				});
				this.rudderstackService.track({
					name: "Savings Button Clicked",
					properties: {
						category: "Trading interaction",
						screen_it_happened_on: "home_screen"
					}
				});
				this.showModal(TradeSavingsComponent, offer);
				return;
			}
			case TRADE_TYPE.STAKE: {
				this.isStakingEnabled$.pipe(take(1)).subscribe((isEnabled: boolean) => {
					if (isEnabled) {
						this.modalService.pop();
						this.stakingEntryHelper.openFlow();
					}
				});
				return;
			}
			// TODO: this will eventually replace the BUY entry point, once the FF is not there please refactor this
			case TRADE_TYPE.CHECKOUT:
			case TRADE_TYPE.LEVERAGE: {
				this.modalService.pop();
				this.router.navigate([{ outlets: { modal: "checkout" } }], {
					...(type === TRADE_TYPE.LEVERAGE && { queryParams: { selectedPill: API_LEVERAGED_TOKEN_TYPE } })
				});
				return;
			}
			default: {
				if (this.vouchersAvailable() && !this.isVerificationMandatory) {
					this.showModal(TradeVoucherComponent, new VoucherOffer());
				}
			}
		}
	}

	public actionTooltip(action: string): string {
		if (action === "buy") {
			if (this.isVerificationMandatory) {
				return "action_buy_unverified_tooltip";
			}
		} else if (action === "sell") {
			if (this.isVerificationMandatory) {
				return "action_sell_unverified_tooltip";
			}
			if (!this.canSell) {
				return "no_crypto_wallet_with_enough_funds_to_sell";
			}
		} else if (action === "swap") {
			if (this.isVerificationMandatory) {
				return "action_swap_unverified_tooltip";
			}
			if (!this.canSell) {
				return "no_wallet_with_enough_funds_to_swap";
			}
		} else if (action === "leverage") {
			if (this.isVerificationMandatory) {
				return "action_buy_unverified_tooltip";
			}
		} else if (action === "savings") {
			if (this.isVerificationMandatory) {
				return "action_savings_plan_unverified_tooltip";
			}
			if (this.unverified) {
				// TODO: After AML5 is activated this can be removed
				return "action_savings_verify_tooltip";
			}
		} else if (action === "voucher") {
			if (this.isVerificationMandatory) {
				return "action_voucher_unverified_tooltip";
			}
			if (!this.vouchersAvailable()) {
				return "no_vouchers_available";
			}
		}
		return "";
	}

	public vouchersAvailable(): boolean {
		const vouchers: PaymentOption[] = this.masterdataService.payment_options
			.filter((p) => p.is_voucher)
			.filter((p) => p.isEnabled());
		return vouchers.length > 0;
	}

	public deposit(): void {
		if (!isSafari()) {
			this.modalService.pop();
			this.fiatDepositEntryService.openDeposit();
		} else {
			// Pop first, then create new popup
			this.modalService.pop();
			setTimeout(() => {
				this.fiatDepositEntryService.openDeposit();
			}, 200);
		}
	}

	public showModal(component, data): void {
		if (!isSafari()) {
			this.modalService.pop();
			const modal = this.modalService.create(component, { data });
			modal.present();
		} else {
			// Pop first, then create new popup
			this.modalService.pop();
			setTimeout(() => {
				const modal = this.modalService.create(component, { data });
				modal.present();
			}, 200);
		}
	}

	public verify(): void {
		if (!this.verificationModalFacade.openVerification()) {
			this.router.navigate(VERIFICATION_MODAL_ROUTE, { skipLocationChange: true });
		}
	}

	private hasSufficientFundsBuy(): boolean {
		return this.userFiatFundService.hasFiatWalletsWithFunds();
	}

	private hasSufficientFundsSell(): boolean {
		const fiat = this.accountService.getDefaultFiat();
		return this.tradablesWithoutIndex()
			.filter((tradable: Tradable) => tradable.isSellActive())
			.some((tradable: Tradable) =>
				this.walletService.getWalletsByTradableId(tradable.getId())?.some((wallet: TradableWallet) => {
					if (wallet instanceof IndexWallet) {
						return wallet.getBalance() >= tradable.getMinSell();
					}
					return wallet.getBalance() >= convertFiatToTradable(fiat, tradable.getMinSell(), tradable);
				})
			);
	}

	public isSwapDisabled(): boolean {
		return CustomModal.injector.get(AccountService).account.getSwapDisabled();
	}

	private tradablesWithoutIndex(): Tradable[] {
		return this.tradableListService.list.filter((t) => !t.isIndexOnly());
	}
}
